// components/ReportStep.tsx
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Fields, WarningLevel } from '../core/_models';
import { validateField } from '../core/validationUtils';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import PortSelect from '../../../shared/components/PortSelect'
import { isPortField } from '../../../shared/utils/portFieldUtils'
import {
  parseCompositePositionValue,
  formatCompositePositionValue,
  CompositePositionParts,
} from '../../../shared/utils/coordinateParsing'
import { ensureFuelChangeoverPresetFields } from './fuelChangeoverReportSchema'

export interface ReportStepRef {
  validateStep: () => boolean;
  getWarnings: () => { hasWarnings: boolean; warningCount: number };
}

interface ReportStepProps {
  sectionTitle: string;
  formSections: Fields[];
  values: Record<string, any>;
  onChange: (label: string, id: string, value: any, isUserInitiated?: boolean) => void;
  onValidate?: (isValid: boolean) => void;
  voyage?: any;
  selectedReport?: any;
  tabularLayout?: boolean;
  // When true (create mode only), render a read-only "Previous consumption" column.
  showPreviousColumn?: boolean;
  // Previous report values keyed by normalized (trim+lowercase) field label.
  previousValues?: Record<string, string>;
  // When true (create mode only), prefill editable voyage fields (departure port,
  // departure date/time, arrival port) from the selected voyage. Fields stay editable.
  autofillVoyageFields?: boolean;
}

// Helper: chunk array into groups of 3
const chunkArray = (array: any[], size: number) => {
  const chunked = [];
  for (let i = 0; i < array.length; i += size) {
    chunked.push(array.slice(i, i + size));
  }
  return chunked;
};

// Normalize a field label for voyage-autofill matching (e.g. "Departure Date & Time" → "departure date time").
const normalizeAutofillLabel = (label: any): string =>
  String(label ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Convert a raw voyage date string into the value format expected by the field's input type.
const toDateInputValue = (raw: any, fieldType?: string): string => {
  const source = String(raw ?? '').trim();
  if (!source) return '';
  const parsed = new Date(source);
  if (isNaN(parsed.getTime())) return source;
  const pad = (n: number) => String(n).padStart(2, '0');
  const datePart = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  const timePart = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
  switch (String(fieldType ?? '').toUpperCase()) {
    case 'DATETIME':
      return `${datePart}T${timePart}`;
    case 'DATE':
      return datePart;
    case 'TIME':
      return timePart;
    default:
      return source;
  }
};

const ReportStep = forwardRef<ReportStepRef, ReportStepProps>(
  ({ sectionTitle, formSections, values, onChange, onValidate, voyage, selectedReport, tabularLayout, showPreviousColumn, previousValues, autofillVoyageFields }, ref) => {

    const [errors, setErrors] = useState<{ [label: string]: string }>({})
    const latestWarningCount = useRef(0);
    const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

    const extractFieldValue = (field: Fields | { id: number | string; label: string }): any => {
      if (!values || !field) return '';
      const idStr = field.id?.toString();
      const idNum = Number(field.id);
      const labelKey = field.label;

      const raw =
        (idStr !== undefined ? values[idStr] : undefined) ??
        (idNum !== undefined && !Number.isNaN(idNum) ? values[idNum] : undefined) ??
        (labelKey ? values[labelKey] : undefined);

      if (raw === undefined || raw === null) return '';
      if (typeof raw === 'object' && !Array.isArray(raw)) {
        if ('value' in raw && raw.value !== undefined && raw.value !== null) {
          return raw.value;
        }
      }
      return raw;
    };

    const extractFieldArrayValue = (field: Fields): any[] => {
      const val = extractFieldValue(field);
      if (Array.isArray(val)) return val;
      if (val !== undefined && val !== null && val !== '') return [val];
      return [];
    };

    const isChangeoverSec = /change\s*over/i.test(sectionTitle);
    const effectiveFormSections = useMemo(() => {
      if (isChangeoverSec) {
        return ensureFuelChangeoverPresetFields(sectionTitle, formSections);
      }
      return formSections;
    }, [isChangeoverSec, sectionTitle, formSections]);

    const orderedFormSections = useMemo(() => {
      return [...effectiveFormSections].sort((left, right) => {
        const leftOrder = left.order ?? Number.MAX_SAFE_INTEGER
        const rightOrder = right.order ?? Number.MAX_SAFE_INTEGER
        if (leftOrder !== rightOrder) return leftOrder - rightOrder
        return Number(left.id ?? 0) - Number(right.id ?? 0)
      })
    }, [effectiveFormSections])

    const validate = () => {
      const newErrors: { [id: string]: string } = {};
      let hasStrictErrors = false;
      let warningCounter = 0; // count SOFT warnings
      const allTouched: Record<string, boolean> = {};

      orderedFormSections.forEach((field) => {
        const fieldValue = extractFieldValue(field);
        const errorMsg = validateField(field, fieldValue);

        if (errorMsg) {
          newErrors[field.id] = errorMsg;
          allTouched[field.id] = true;

          if (field.warningOnly === WarningLevel.STRICT) hasStrictErrors = true;
          else if (field.warningOnly === WarningLevel.SOFT) warningCounter++;
        }
      });

      latestWarningCount.current = warningCounter; // ✅ update the ref

      setErrors(newErrors);
      setTouchedFields(prev => ({ ...prev, ...allTouched })); // show all errors

      if (onValidate) onValidate(!hasStrictErrors);

      return !hasStrictErrors;
    };

    // ✅ Expose validate function to parent via ref
    useImperativeHandle(ref, () => ({
      validateStep: validate,
      getWarnings: () => ({
        hasWarnings: latestWarningCount.current > 0,
        warningCount: latestWarningCount.current,
      }),
    }));

    useEffect(() => {
      validate();
    }, [orderedFormSections, values]);

    useEffect(() => {
      orderedFormSections.forEach((field) => {
        const isReadOnly = field.readOnly;
        const currentValue = extractFieldValue(field);

        const valueMap: Record<string, any> = {
          'Vessel Name': voyage?.vessel?.fleet_name,
          'Voyage Number': voyage?.voyageNumber,
          'Report Type': selectedReport?.name,
          'Departure Port': voyage?.departurePort,
          'Departure Datetime': voyage?.startDate,
          'Arrival Port': voyage?.arrivalPort,
          'ETA Datetime': voyage?.endDate,
        };

        const shouldPopulate =
          isReadOnly &&
          valueMap[field.label] !== undefined &&
          (currentValue === undefined || currentValue === '');

        if (shouldPopulate) {
          onChange(field.label, field.id.toString(), valueMap[field.label], false);
          return;
        }

        // ✅ Create mode: prefill voyage details into editable fields so the user
        // can review/adjust them instead of retyping.
        if (!autofillVoyageFields || isReadOnly) return;
        if (currentValue !== undefined && currentValue !== '') return;

        const normalizedLabel = normalizeAutofillLabel(field.label);
        let autofillValue: any;
        if (normalizedLabel === 'departure port') {
          autofillValue = voyage?.departurePort;
        } else if (normalizedLabel === 'arrival port') {
          autofillValue = voyage?.arrivalPort;
        } else if (
          normalizedLabel === 'departure datetime' ||
          normalizedLabel === 'departure date time' ||
          normalizedLabel === 'departure date and time' ||
          normalizedLabel === 'departure date' ||
          normalizedLabel === 'departure time'
        ) {
          autofillValue = toDateInputValue(voyage?.startDate, field.fieldType);
        }

        if (autofillValue !== undefined && autofillValue !== null && String(autofillValue).trim() !== '') {
          onChange(field.label, field.id.toString(), autofillValue, false);
        }
      });
    }, [orderedFormSections, voyage?.id]);

    const renderField = (field: Fields) => {
      const fieldValue = extractFieldValue(field);
      const commonProps = {
        id: field.id.toString(),
        name: field.id.toString(),
        value: fieldValue,
        onChange: (e: React.ChangeEvent<any>) => onChange(field.label, field.id.toString(), e.target.value, true),
        className: 'form-control grey-input',
      };

      switch (field.fieldType.toUpperCase()) {
        case 'TEXT': {
          if (isPortField(field.label)) {
            const rawVal = values[field.id]?.value ?? (typeof values[field.id] === 'string' ? values[field.id] : '');
            return (
              <PortSelect
                id={field.id.toString()}
                name={field.id.toString()}
                value={rawVal}
                disabled={field.readOnly}
                placeholder={`Select or search ${field.label.toLowerCase()}...`}
                onBlur={() => {
                  setTouchedFields((prev) => ({ ...prev, [field.id]: true }));
                }}
                onChange={(e: any) => {
                  const newValue = typeof e === 'string' ? e : (e?.target?.value ?? '');
                  onChange(field.label, field.id.toString(), newValue);

                  const errorMsg = validateField(field, newValue);
                  setErrors((prevErrors) => {
                    const newErrors = { ...prevErrors };
                    if (errorMsg) {
                      newErrors[field.id] = errorMsg;
                    } else {
                      delete newErrors[field.id];
                    }
                    return newErrors;
                  });
                }}
              />
            );
          }

          return (
            <div className="input-group">
              <input
                type="text"
                disabled={field.readOnly}
                id={field.id.toString()}
                value={values[field.id]?.value ?? ''}
                minLength={field.minValue} // ✅ For min length validation
                maxLength={field.maxValue} // ✅ For max length validation
                onBlur={() => {
                  setTouchedFields((prev) => ({ ...prev, [field.id]: true }));
                }}
                onChange={(e) => {
                  const newValue = e.target.value;
                  onChange(field.label, field.id.toString(), newValue);

                  const errorMsg = validateField(field, newValue);
                  setErrors((prevErrors) => {
                    const newErrors = { ...prevErrors };
                    if (errorMsg) {
                      newErrors[field.id] = errorMsg;
                    } else {
                      delete newErrors[field.id];
                    }
                    return newErrors;
                  });
                }}
                className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              />

              {/* ✅ Show unit like NUMBER field */}
              {field.unit && <span className="input-group-text">{field.unit}</span>}
            </div>
          );
        }
        case 'DATE':
        case 'TIME':
        case 'DATETIME': {
          const inputType =
            field.fieldType.toUpperCase() === 'DATETIME'
              ? 'datetime-local'
              : field.fieldType.toLowerCase();

          return (
            <div className="input-group">
              <input
                type={inputType}
                disabled={field.readOnly}
                id={field.id.toString()}
                value={fieldValue}
                minLength={field.minValue} // ✅ For min length validation
                maxLength={field.maxValue} // ✅ For max length validation
                onBlur={() => {
                  setTouchedFields((prev) => ({ ...prev, [field.id]: true }));
                }}
                onChange={(e) => {
                  const newValue = e.target.value;
                  onChange(field.label, field.id.toString(), newValue, true);

                  const errorMsg = validateField(field, newValue);
                  setErrors((prevErrors) => {
                    const newErrors = { ...prevErrors };
                    if (errorMsg) {
                      newErrors[field.id] = errorMsg;
                    } else {
                      delete newErrors[field.id];
                    }
                    return newErrors;
                  });
                }}
                className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              />

              {/* ✅ Show unit like NUMBER field */}
              {field.unit && <span className="input-group-text">{field.unit}</span>}
            </div>
          );
        }
        case 'NUMBER':
          return (
            <div className="input-group">
              <input
                type="number"
                className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
                value={fieldValue}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, [field.id]: true }));
                }}
                onChange={(e) => {
                  const newValue = e.target.value;
                  onChange(field.label, field.id.toString(), newValue, true);

                  const errorMsg = validateField(field, newValue);
                  setErrors((prevErrors) => {
                    const newErrors = { ...prevErrors };
                    if (errorMsg) {
                      newErrors[field.id] = errorMsg;
                    } else {
                      delete newErrors[field.id];
                    }
                    return newErrors;
                  });
                }} required={field.required}
              />
              {field.unit && <span className="input-group-text">{field.unit}</span>}
            </div>
          );
        case 'TEXTAREA':
          return <textarea {...commonProps} rows={3} />;
        case 'SELECT':
          return (
            <SearchableSelect value={fieldValue}
              onBlur={() => {
                setTouchedFields(prev => ({ ...prev, [field.id]: true }));
              }}
              onChange={(e) => {
                const newValue = e.target.value;
                onChange(field.label, field.id.toString(), newValue, true);

                const errorMsg = validateField(field, newValue);
                setErrors((prevErrors) => {
                  const newErrors = { ...prevErrors };
                  if (errorMsg) {
                    newErrors[field.id] = errorMsg;
                  } else {
                    delete newErrors[field.id];
                  }
                  return newErrors;
                });
              }} className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}>
              <option value="">Select</option>
              {field.optionsJson?.map(opt => {
                if (typeof opt === 'string') {
                  return <option key={opt} value={opt}>{opt}</option>;
                }
              })}
            </SearchableSelect>
          );
        case 'RADIO':
          return (
            <div>
              {field.optionsJson?.map(opt => (
                <label key={opt} className="me-3">
                  <input
                    type="radio"
                    name={field.id.toString()}
                    value={opt}
                    checked={fieldValue === opt}
                    onBlur={() => {
                      setTouchedFields(prev => ({ ...prev, [field.id]: true }));
                    }}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      onChange(field.label, field.id.toString(), newValue, true);

                      const errorMsg = validateField(field, newValue);
                      setErrors((prevErrors) => {
                        const newErrors = { ...prevErrors };
                        if (errorMsg) {
                          newErrors[field.id] = errorMsg;
                        } else {
                          delete newErrors[field.id];
                        }
                        return newErrors;
                      });
                    }} className="me-1"
                  />
                  {opt}
                </label>
              ))}
            </div>
          );

        case 'CHECKBOX':
          return (
            <div>
              {field.optionsJson?.map(opt => (
                <label key={opt} className="me-3">
                  <input
                    type="checkbox"
                    value={opt}
                    checked={extractFieldArrayValue(field).includes(opt)}
                    onBlur={() => {
                      setTouchedFields(prev => ({ ...prev, [field.id]: true }));
                    }}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const currentValues = extractFieldArrayValue(field);
                      const updated = checked
                        ? [...currentValues, opt]
                        : currentValues.filter((v: string) => v !== opt);
                      onChange(field.label, field.id.toString(), updated, true);
                    }}
                    className="me-1"
                  />
                  {opt}
                </label>
              ))}
            </div>
          );

        default: {
          if (isPortField(field.label)) {
            const rawVal = values[field.id]?.value ?? (typeof values[field.id] === 'string' ? values[field.id] : '');
            return (
              <PortSelect
                id={field.id.toString()}
                name={field.id.toString()}
                value={rawVal}
                disabled={field.readOnly}
                placeholder={`Select or search ${field.label.toLowerCase()}...`}
                onBlur={() => {
                  setTouchedFields((prev) => ({ ...prev, [field.id]: true }));
                }}
                onChange={(e: any) => {
                  const newValue = typeof e === 'string' ? e : (e?.target?.value ?? '');
                  onChange(field.label, field.id.toString(), newValue);
                }}
              />
            );
          }
          return <input type="text" {...commonProps} />;
        }
      }
    };

    // ✅ Helper function to format geo labels dynamically
    const formatGeoLabel = (label: string) => {
      const lowerLabel = label.toLowerCase();

      if (lowerLabel.includes("latitude")) {
        // Extract prefix before "latitude"
        const prefix = lowerLabel.split("latitude")[0].trim(); // e.g., "start position" or "stop position"
        return `${prefix ? capitalizeWords(prefix) + " " : ""}Latitude`;
      }

      if (lowerLabel.includes("longitude")) {
        const prefix = lowerLabel.split("longitude")[0].trim();
        return `${prefix ? capitalizeWords(prefix) + " " : ""}Longitude`;
      }

      return label;
    };

    // ✅ Capitalize each word of a prefix (e.g., "start position" → "Start Position")
    const capitalizeWords = (str: string) =>
      str.replace(/\b\w/g, (char) => char.toUpperCase());

    // --- Geo helpers (group start/stop/generic + consistent order) ---
const GEO_PART_RE = /(deg|min|sec|direction)/i;
const normalize = (s: string) => s.toLowerCase().replace(/postion/g, 'position');

type GeoGroups = {
  lat: { start: Fields[]; stop: Fields[]; generic: Fields[] };
  lon: { start: Fields[]; stop: Fields[]; generic: Fields[] };
};

const groupGeoFields = (fields: Fields[]): GeoGroups => {
  const groups: GeoGroups = {
    lat: { start: [], stop: [], generic: [] },
    lon: { start: [], stop: [], generic: [] },
  };

  fields.forEach((f) => {
    const l = normalize(f.label);
    const isLat = l.includes('latitude');
    const isLon = l.includes('longitude');
    const isGeoPart = GEO_PART_RE.test(l);

    if (!(isLat || isLon) || !isGeoPart) return;

    const bucket = isLat ? groups.lat : groups.lon;
    if (l.includes('start')) bucket.start.push(f);
    else if (l.includes('stop')) bucket.stop.push(f);
    else bucket.generic.push(f);
  });

  const order = ['deg', 'min', 'sec', 'direction'];
  const sortFn = (a: Fields, b: Fields) =>
    order.findIndex((k) => normalize(a.label).includes(k)) -
    order.findIndex((k) => normalize(b.label).includes(k));

  (['start', 'stop', 'generic'] as const).forEach((k) => {
    groups.lat[k].sort(sortFn);
    groups.lon[k].sort(sortFn);
  });

  return groups;
};

// Render one geo field cell styled cleanly with inline units (Deg, min, sec, Direction dropdown)
const getGeoPartSuffix = (label: string): string => {
  const l = normalize(label);
  if (l.includes('deg')) return 'Deg';
  if (l.includes('min')) return 'min';
  if (l.includes('sec')) return 'sec';
  return '';
};

const renderGeoField = (
  field: Fields,
  errors: Record<string, string>,
  values: { [fieldId: number]: { value: string; label: string } },
  onChange: (label: string, id: string, value: any, isUserInitiated?: boolean) => void,
  setTouchedFields: React.Dispatch<React.SetStateAction<Record<number, boolean>>>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  validateField: (field: Fields, value: any) => string | null
) => {
  const isDir = normalize(field.label).includes('direction') || field.fieldType.toUpperCase() === 'SELECT';
  const suffix = getGeoPartSuffix(field.label);

  if (isDir) {
    return (
      <div key={field.id} style={{ flex: '0 0 auto', width: '90px', minWidth: '75px' }}>
        <select
          id={field.id.toString()}
          disabled={field.readOnly}
          className={`form-select ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
          style={{
            height: '42px',
            borderRadius: '8px',
            borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
            backgroundColor: '#fff',
            fontSize: '14px',
            fontWeight: 500,
          }}
          value={extractFieldValue(field)}
          onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
          onChange={(e) => {
            const newValue = e.target.value;
            onChange(field.label, field.id.toString(), newValue, true);
            const errorMsg = validateField(field, newValue);
            setErrors((prevErrors) => {
              const newErrors = { ...prevErrors };
              if (errorMsg) newErrors[field.id] = errorMsg;
              else delete newErrors[field.id];
              return newErrors;
            });
          }}
        >
          <option value="">--</option>
          {field.optionsJson?.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          )) || (normalize(field.label).includes('latitude') ? (
            <>
              <option value="N">N</option>
              <option value="S">S</option>
            </>
          ) : (
            <>
              <option value="E">E</option>
              <option value="W">W</option>
            </>
          ))}
        </select>
        {errors[field.id] && (
          <div className={`mt-1 small ${errors[field.id].startsWith('⚠️') ? 'text-warning' : 'text-danger'}`}>
            {errors[field.id]}
          </div>
        )}
      </div>
    );
  }

  return (
    <div key={field.id} className="d-flex align-items-center" style={{ flex: '0 0 auto' }}>
      <div>
        <input
          type="number"
          id={field.id.toString()}
          disabled={field.readOnly}
          className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
          style={{
            width: '90px',
            minWidth: '70px',
            height: '42px',
            borderRadius: '8px',
            borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
            backgroundColor: '#fff',
            textAlign: 'center',
            fontSize: '14px',
          }}
          value={extractFieldValue(field)}
          onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
          onChange={(e) => {
            const newValue = e.target.value;
            onChange(field.label, field.id.toString(), newValue, true);
            const errorMsg = validateField(field, newValue);
            setErrors((prevErrors) => {
              const newErrors = { ...prevErrors };
              if (errorMsg) newErrors[field.id] = errorMsg;
              else delete newErrors[field.id];
              return newErrors;
            });
          }}
        />
        {errors[field.id] && (
          <div className={`mt-1 small ${errors[field.id].startsWith('⚠️') ? 'text-warning' : 'text-danger'}`}>
            {errors[field.id]}
          </div>
        )}
      </div>
      {suffix && (
        <span className="text-muted fw-semibold ms-2 me-3" style={{ fontSize: '14px', userSelect: 'none' }}>
          {suffix}
        </span>
      )}
    </div>
  );
};

const isCompositePositionField = (field: Fields | { label: string; fieldType?: string }): boolean => {
  if (!field || !field.label) return false;

  const type = String(field.fieldType ?? '').toUpperCase();
  // Standard input types are NEVER composite position fields
  if (['DATETIME', 'DATE', 'TIME', 'SELECT', 'RADIO', 'CHECKBOX', 'TEXTAREA'].includes(type)) {
    return false;
  }

  const l = normalize(field.label);

  // Individual degree/minute/second/direction parts are not composite fields
  if (GEO_PART_RE.test(l) && (l.includes('latitude') || l.includes('longitude'))) {
    return false;
  }

  // Explicit composite types
  if (['POSITION', 'COORDINATE', 'COORDINATES', 'LAT_LONG', 'LAT_LON'].includes(type)) {
    return true;
  }

  // Label patterns matching composite position fields specifically
  return (
    l.includes('lat/long') ||
    l.includes('lat/lon') ||
    l.includes('lat & long') ||
    l.includes('lat & lon') ||
    l.includes('latitude/longitude') ||
    l.includes('latitude & longitude') ||
    l.includes('composite position') ||
    l.includes('vessel position') ||
    l.includes('ship position') ||
    l.includes('current position') ||
    l.includes('present position') ||
    l.includes('anchorage position') ||
    l.includes('berth position') ||
    l.includes('cosp position') ||
    l.includes('eosp position') ||
    l === 'position' ||
    l.startsWith('position')
  );
};

// ✅ Group geo fields by Start/Stop/Generic and by Lat/Lon
const geo = groupGeoFields(orderedFormSections);

// Helper to determine active ECA event selection from form values
const getEcaSelection = (valuesRecord?: Record<string, any>): string => {
  if (!valuesRecord) return '';
  for (const [key, entry] of Object.entries(valuesRecord)) {
    const label = String(entry?.label || key).toLowerCase();
    if (
      label.includes('eca type') ||
      label.includes('event type') ||
      label.includes('changeover direction') ||
      label.includes('sequence step') ||
      label.includes('eca event')
    ) {
      const val = String(entry?.value ?? entry ?? '').toLowerCase();
      if (val) return val;
    }
  }
  return '';
};

// ✅ Composite position fields (e.g. "Position Start Change Over", "Position at Commencement of Changeover")
const compositePositionFields = useMemo(() => {
  const seenLabels = new Set<string>();
  const ecaSelection = getEcaSelection(values);
  const isExitSelected = ecaSelection.includes('exit');
  const isEntrySelected = ecaSelection.includes('entry') || ecaSelection.includes('enter');

  return orderedFormSections.filter((field) => {
    if (!isCompositePositionField(field)) return false;
    const key = normalize(field.label);

    // Dynamic visibility: show ECA Exit position only when Exit is selected, else show ECA Entry position
    if (key.includes('eca exit')) {
      if (!isExitSelected) return false;
    } else if (key.includes('eca entry') || key.includes('eca enrty')) {
      if (isExitSelected && !isEntrySelected) return false;
    }

    if (seenLabels.has(key)) return false;
    seenLabels.add(key);
    return true;
  });
}, [orderedFormSections, values]);

// ✅ Remaining other (non-geo-part and non-composite-position) fields
const otherFields = useMemo(() => {
  const ecaSelection = getEcaSelection(values);
  const isExitSelected = ecaSelection.includes('exit');
  const isEntrySelected = ecaSelection.includes('entry') || ecaSelection.includes('enter');

  return orderedFormSections.filter((field) => {
    const l = normalize(field.label);
    const isGeoPart = (l.includes('latitude') || l.includes('longitude')) && GEO_PART_RE.test(l);
    const isCompositePos = isCompositePositionField(field);

    if (l.includes('eca exit')) {
      if (!isExitSelected) return false;
    } else if (l.includes('eca entry') || l.includes('eca enrty')) {
      if (isExitSelected && !isEntrySelected) return false;
    }

    return !isGeoPart && !isCompositePos;
  });
}, [orderedFormSections, values]);

const isPrimarySelectorField = (field: Fields) => {
  const l = normalize(field.label);
  return (
    l.includes('eca type') ||
    l.includes('event type') ||
    l.includes('changeover direction') ||
    l.includes('sequence step') ||
    l.includes('eca zone')
  );
};

const primarySelectorFields = useMemo(() => {
  return otherFields.filter((f) => isPrimarySelectorField(f));
}, [otherFields]);

const remainingOtherFields = useMemo(() => {
  return otherFields.filter((f) => !isPrimarySelectorField(f));
}, [otherFields]);

const remainingFieldChunks = chunkArray(remainingOtherFields, 3);

// Local styles for the geo strips (scoped to this component only)
const scrollRowStyle: React.CSSProperties = {
  display: 'flex',
  overflowX: 'auto',
  flexWrap: 'nowrap',
  gap: '24px',
  padding: '6px 0',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'thin',
};

const clusterColStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 'fit-content',
};

const fieldRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '4px',
};

const isChangeoverSection = /change\s*over/i.test(sectionTitle);

const renderCompositePositionField = (field: Fields) => {
  const rawValue = extractFieldValue(field);
  const parts = parseCompositePositionValue(rawValue);

  const handlePartChange = (partKey: keyof CompositePositionParts, val: string) => {
    const nextParts: CompositePositionParts = {
      ...parts,
      [partKey]: val,
    };
    const formatted = formatCompositePositionValue(nextParts);
    onChange(field.label, field.id.toString(), formatted, true);

    const latDegNum = parseFloat(nextParts.latDeg);
    const latMinNum = parseFloat(nextParts.latMin);
    const latSecNum = parseFloat(nextParts.latSec);
    const lonDegNum = parseFloat(nextParts.lonDeg);
    const lonMinNum = parseFloat(nextParts.lonMin);
    const lonSecNum = parseFloat(nextParts.lonSec);

    let errorMsg: string | null = null;
    if (nextParts.latDeg && (isNaN(latDegNum) || latDegNum < 0 || latDegNum > 90)) {
      errorMsg = 'Latitude degrees must be between 0 and 90.';
    } else if (nextParts.latMin && (isNaN(latMinNum) || latMinNum < 0 || latMinNum >= 60)) {
      errorMsg = 'Latitude minutes must be between 0 and 59.';
    } else if (nextParts.latSec && (isNaN(latSecNum) || latSecNum < 0 || latSecNum >= 60)) {
      errorMsg = 'Latitude seconds must be between 0 and 59.99.';
    } else if (nextParts.lonDeg && (isNaN(lonDegNum) || lonDegNum < 0 || lonDegNum > 180)) {
      errorMsg = 'Longitude degrees must be between 0 and 180.';
    } else if (nextParts.lonMin && (isNaN(lonMinNum) || lonMinNum < 0 || lonMinNum >= 60)) {
      errorMsg = 'Longitude minutes must be between 0 and 59.';
    } else if (nextParts.lonSec && (isNaN(lonSecNum) || lonSecNum < 0 || lonSecNum >= 60)) {
      errorMsg = 'Longitude seconds must be between 0 and 59.99.';
    }

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (errorMsg) {
        newErrors[field.id] = errorMsg;
      } else {
        delete newErrors[field.id];
      }
      return newErrors;
    });
  };

  return (
    <div key={field.id} className="border rounded-3 p-4 mb-4 bg-white shadow-xs">
      <div className="text-dark fw-bold mb-3" style={{ fontSize: '15px' }}>
        {field.label} {field.required && <span className="text-danger">*</span>}
      </div>

      {/* Row 1: Latitude */}
      <div className="mb-3">
        <div className="text-muted fw-semibold mb-2" style={{ fontSize: '13px' }}>
          Latitude <span className="text-danger">*</span>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <input
              type="number"
              disabled={field.readOnly}
              placeholder="00"
              className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              style={{
                width: '90px',
                minWidth: '70px',
                height: '42px',
                borderRadius: '8px',
                borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
                backgroundColor: '#fff',
                textAlign: 'center',
                fontSize: '14px',
              }}
              value={parts.latDeg}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
              onChange={(e) => handlePartChange('latDeg', e.target.value)}
            />
            <span className="text-muted fw-semibold ms-2 me-3" style={{ fontSize: '14px' }}>
              Deg
            </span>
          </div>
          <div className="d-flex align-items-center">
            <input
              type="number"
              disabled={field.readOnly}
              placeholder="00"
              className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              style={{
                width: '90px',
                minWidth: '70px',
                height: '42px',
                borderRadius: '8px',
                borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
                backgroundColor: '#fff',
                textAlign: 'center',
                fontSize: '14px',
              }}
              value={parts.latMin}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
              onChange={(e) => handlePartChange('latMin', e.target.value)}
            />
            <span className="text-muted fw-semibold ms-2 me-3" style={{ fontSize: '14px' }}>
              min
            </span>
          </div>
          <div className="d-flex align-items-center">
            <input
              type="number"
              step="any"
              disabled={field.readOnly}
              placeholder="00.0"
              className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              style={{
                width: '90px',
                minWidth: '70px',
                height: '42px',
                borderRadius: '8px',
                borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
                backgroundColor: '#fff',
                textAlign: 'center',
                fontSize: '14px',
              }}
              value={parts.latSec}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
              onChange={(e) => handlePartChange('latSec', e.target.value)}
            />
            <span className="text-muted fw-semibold ms-2 me-3" style={{ fontSize: '14px' }}>
              sec
            </span>
          </div>
          <div style={{ width: '90px', minWidth: '75px' }}>
            <select
              disabled={field.readOnly}
              className={`form-select ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              style={{
                height: '42px',
                borderRadius: '8px',
                borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
                backgroundColor: '#fff',
                fontSize: '14px',
                fontWeight: 500,
              }}
              value={parts.latDir || 'N'}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
              onChange={(e) => handlePartChange('latDir', e.target.value as 'N' | 'S')}
            >
              <option value="N">N</option>
              <option value="S">S</option>
            </select>
          </div>
        </div>
      </div>

      {/* Row 2: Longitude */}
      <div>
        <div className="text-muted fw-semibold mb-2" style={{ fontSize: '13px' }}>
          Longitude <span className="text-danger">*</span>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <input
              type="number"
              disabled={field.readOnly}
              placeholder="000"
              className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              style={{
                width: '90px',
                minWidth: '70px',
                height: '42px',
                borderRadius: '8px',
                borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
                backgroundColor: '#fff',
                textAlign: 'center',
                fontSize: '14px',
              }}
              value={parts.lonDeg}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
              onChange={(e) => handlePartChange('lonDeg', e.target.value)}
            />
            <span className="text-muted fw-semibold ms-2 me-3" style={{ fontSize: '14px' }}>
              Deg
            </span>
          </div>
          <div className="d-flex align-items-center">
            <input
              type="number"
              disabled={field.readOnly}
              placeholder="00"
              className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              style={{
                width: '90px',
                minWidth: '70px',
                height: '42px',
                borderRadius: '8px',
                borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
                backgroundColor: '#fff',
                textAlign: 'center',
                fontSize: '14px',
              }}
              value={parts.lonMin}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
              onChange={(e) => handlePartChange('lonMin', e.target.value)}
            />
            <span className="text-muted fw-semibold ms-2 me-3" style={{ fontSize: '14px' }}>
              min
            </span>
          </div>
          <div className="d-flex align-items-center">
            <input
              type="number"
              step="any"
              disabled={field.readOnly}
              placeholder="00.0"
              className={`form-control ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              style={{
                width: '90px',
                minWidth: '70px',
                height: '42px',
                borderRadius: '8px',
                borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
                backgroundColor: '#fff',
                textAlign: 'center',
                fontSize: '14px',
              }}
              value={parts.lonSec}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
              onChange={(e) => handlePartChange('lonSec', e.target.value)}
            />
            <span className="text-muted fw-semibold ms-2 me-3" style={{ fontSize: '14px' }}>
              sec
            </span>
          </div>
          <div style={{ width: '90px', minWidth: '75px' }}>
            <select
              disabled={field.readOnly}
              className={`form-select ${field.readOnly ? 'readonly-input' : 'grey-input'}`}
              style={{
                height: '42px',
                borderRadius: '8px',
                borderColor: errors[field.id] ? '#f1416c' : '#e4e6ef',
                backgroundColor: '#fff',
                fontSize: '14px',
                fontWeight: 500,
              }}
              value={parts.lonDir || 'E'}
              onBlur={() => setTouchedFields((prev) => ({ ...prev, [field.id]: true }))}
              onChange={(e) => handlePartChange('lonDir', e.target.value as 'E' | 'W')}
            >
              <option value="E">E</option>
              <option value="W">W</option>
            </select>
          </div>
        </div>
      </div>

      {errors[field.id] && (
        <div className={`mt-2 small ${errors[field.id].startsWith('⚠️') ? 'text-warning' : 'text-danger'}`}>
          {errors[field.id]}
        </div>
      )}
    </div>
  );
};

return (
  <div className="px-3">
    <div className="d-flex justify-content-between align-items-center py-3">
      <h4 className="fw-bold text-dark m-0">{sectionTitle}</h4>
    </div>

    {/* ✅ Voyage Event Sequence (Fuel Changeover) Interactive Flow Banner */}
    {isChangeoverSection && (
      <div className="border rounded-3 p-4 mb-4 bg-light">
        <div className="fw-bold text-dark mb-2">
          <i className="bi bi-diagram-3-fill text-primary me-2"></i>
          Voyage Event Sequence (Fuel Changeover & ECA Events)
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <div className="p-3 bg-white rounded border">
              <div className="small fw-bold text-primary mb-2">ECA Entry Sequence Flow:</div>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="badge badge-light-primary px-3 py-2 fw-semibold">1. Commencement</span>
                <i className="bi bi-arrow-right text-muted"></i>
                <span className="badge badge-light-warning px-3 py-2 fw-semibold">2. Completion</span>
                <i className="bi bi-arrow-right text-muted"></i>
                <span className="badge badge-light-success px-3 py-2 fw-semibold">3. ECA Entry</span>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="p-3 bg-white rounded border">
              <div className="small fw-bold text-danger mb-2">ECA Exit Sequence Flow:</div>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="badge badge-light-danger px-3 py-2 fw-semibold">1. ECA Exit</span>
                <i className="bi bi-arrow-right text-muted"></i>
                <span className="badge badge-light-primary px-3 py-2 fw-semibold">2. Commencement</span>
                <i className="bi bi-arrow-right text-muted"></i>
                <span className="badge badge-light-success px-3 py-2 fw-semibold">3. Completion</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* ✅ Primary Selector Fields (e.g. ECA Type / Changeover Event Type) render FIRST at top */}
    {primarySelectorFields.length > 0 && (
      <div className="row gx-5 gy-4 mb-4">
        {primarySelectorFields.map((field, index) => (
          <div className="col-md-4" key={field.id || index}>
            <div className="text-muted mb-1 fw-semibold">
              {field.label} {field.required && <span className="text-danger">*</span>}
            </div>
            {renderField(field)}
            {errors[field.id] && (
              <div className={`mt-1 small ${errors[field.id].startsWith('⚠️') ? 'text-warning' : 'text-danger'}`}>
                {errors[field.id]}
              </div>
            )}
          </div>
        ))}
      </div>
    )}

    {/* ✅ Composite Position Fields (Degree Minute Second Direction inputs) */}
    {compositePositionFields.length > 0 && (
      <div className="mb-4">
        {compositePositionFields.map((field) => renderCompositePositionField(field))}
      </div>
    )}

    {/* ✅ Tabular layout (Sr. No | Parameter | Value) mirrors the report view */}
    {tabularLayout ? (
      <div className="table-responsive">
        <table className="table table-bordered table-row-bordered align-middle mb-0">
          <thead>
            <tr className="fw-bold text-muted bg-light">
              <th className="text-center" style={{ width: 80 }}>Sr. No</th>
              <th>Parameter</th>
              {showPreviousColumn && (
                <th style={{ width: '25%' }}>Previous consumption</th>
              )}
              <th style={{ width: '45%' }}>Value</th>
            </tr>
          </thead>
          <tbody>
            {remainingOtherFields.map((field, idx) => (
              <tr key={field.id || idx}>
                <td className="text-center">{idx + 1}</td>
                <td>
                  {field.label} {field.required && <span className="text-danger">*</span>}
                </td>
                {showPreviousColumn && (
                  <td>
                    {previousValues?.[`id:${field.id}`] ??
                      previousValues?.[`label:${String(field.label).trim().toLowerCase()}`] ??
                      '--'}
                  </td>
                )}
                <td>
                  {renderField(field)}
                  {errors[field.id] && (
                    <div className={`mt-1 small ${errors[field.id].startsWith('⚠️') ? 'text-warning' : 'text-danger'}`}>
                      {errors[field.id]}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      /* ✅ Render remaining other fields in 3-column layout */
      remainingFieldChunks.map((chunk, rowIndex) => (
        <div className="row gx-5 gy-4 mb-3" key={rowIndex}>
          {chunk.map((field, colIndex) => (
            <div className="col-md-4" key={field.id || colIndex}>
              <div className="text-muted mb-1">
                {field.label} {field.required && <span className="text-danger">*</span>}
              </div>
              {renderField(field)}
              {errors[field.id] && (
                <div className={`mt-1 small ${errors[field.id].startsWith('⚠️') ? 'text-warning' : 'text-danger'}`}>
                  {errors[field.id]}
                </div>
              )}
            </div>
          ))}
        </div>
      ))
    )}

    {/* === Row 1: Latitude (Inline Deg min sec Direction) === */}
    {(geo.lat.start.length > 0 || geo.lat.stop.length > 0 || geo.lat.generic.length > 0) && (
      <div className="mb-4">
        <div style={scrollRowStyle}>
          {/* Start or Generic Latitude cluster */}
          {(geo.lat.start.length > 0 || geo.lat.generic.length > 0) && (
            <div style={clusterColStyle}>
              <div className="text-dark fw-semibold mb-2" style={{ fontSize: '14px' }}>
                {geo.lat.start.length ? 'Start Position Latitude' : 'Latitude'} <span className="text-danger">*</span>
              </div>
              <div style={fieldRowStyle}>
                {(geo.lat.start.length ? geo.lat.start : geo.lat.generic).map((f) =>
                  renderGeoField(
                    f,
                    errors,
                    values,
                    onChange,
                    setTouchedFields,
                    setErrors,
                    validateField
                  )
                )}
              </div>
            </div>
          )}

          {/* Stop Latitude cluster */}
          {geo.lat.stop.length > 0 && (
            <div style={clusterColStyle}>
              <div className="text-dark fw-semibold mb-2" style={{ fontSize: '14px' }}>
                Stop Position Latitude <span className="text-danger">*</span>
              </div>
              <div style={fieldRowStyle}>
                {geo.lat.stop.map((f) =>
                  renderGeoField(
                    f,
                    errors,
                    values,
                    onChange,
                    setTouchedFields,
                    setErrors,
                    validateField
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* === Row 2: Longitude (Inline Deg min sec Direction) === */}
    {(geo.lon.start.length > 0 || geo.lon.stop.length > 0 || geo.lon.generic.length > 0) && (
      <div className="mb-4">
        <div style={scrollRowStyle}>
          {/* Start or Generic Longitude cluster */}
          {(geo.lon.start.length > 0 || geo.lon.generic.length > 0) && (
            <div style={clusterColStyle}>
              <div className="text-dark fw-semibold mb-2" style={{ fontSize: '14px' }}>
                {geo.lon.start.length ? 'Start Position Longitude' : 'Longitude'} <span className="text-danger">*</span>
              </div>
              <div style={fieldRowStyle}>
                {(geo.lon.start.length ? geo.lon.start : geo.lon.generic).map((f) =>
                  renderGeoField(
                    f,
                    errors,
                    values,
                    onChange,
                    setTouchedFields,
                    setErrors,
                    validateField
                  )
                )}
              </div>
            </div>
          )}

          {/* Stop Longitude cluster */}
          {geo.lon.stop.length > 0 && (
            <div style={clusterColStyle}>
              <div className="text-dark fw-semibold mb-2" style={{ fontSize: '14px' }}>
                Stop Position Longitude <span className="text-danger">*</span>
              </div>
              <div style={fieldRowStyle}>
                {geo.lon.stop.map((f) =>
                  renderGeoField(
                    f,
                    errors,
                    values,
                    onChange,
                    setTouchedFields,
                    setErrors,
                    validateField
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )}



      </div>
    );
  }
);

export default ReportStep;
